import {
  MutationCache,
  QueryClient,
  type MutationFunctionContext,
  type MutationOptions,
} from '@tanstack/react-query';
import type { ApiError } from '../api/http';
import { AccountChangedError, boundSubject } from '../api/authSession';

export interface AppRouterContext {
  queryClient: QueryClient;
}

const isApiError = (error: unknown): error is ApiError =>
  error instanceof Error && 'status' in error && typeof error.status === 'number';

export const shouldRetryQuery = (failureCount: number, error: unknown): boolean => {
  if (failureCount >= 2) return false;
  if (!isApiError(error)) return true;
  if (error.status === 408 || error.status === 429) return true;
  return error.status < 400 || error.status >= 500;
};

/** The account each mutation was scheduled under, keyed by its per-run function context. */
const scheduledSubject = new WeakMap<object, string | null>();

/**
 * Runs every mutation only for the account it was scheduled under. A mutation
 * can run later than `mutate()` (paused while offline, then resumed), and by
 * then the signed-in account may be a different one; it is refused before any
 * token is fetched instead of sending the first account's change with the
 * next account's token.
 */
class AccountQueryClient extends QueryClient {
  override defaultMutationOptions<T extends MutationOptions<any, any, any, any>>(options?: T): T {
    const defaulted = super.defaultMutationOptions(options);
    const run = defaulted.mutationFn;
    if (defaulted === options || !run) return defaulted;
    return {
      ...defaulted,
      mutationFn: (variables: unknown, context: MutationFunctionContext) => {
        if (scheduledSubject.get(context) !== boundSubject()) {
          return Promise.reject(new AccountChangedError());
        }
        return run(variables, context);
      },
    };
  }
}

/** One client per router instance. TanStack Start creates a router per SSR
 * request, preventing server data and errors from leaking between requests. */
export const createAppQueryClient = (): QueryClient =>
  new AccountQueryClient({
    // Called synchronously by mutate(), before any pause, with the context the
    // mutation function later receives.
    mutationCache: new MutationCache({
      onMutate: (_variables, _mutation, context) => {
        scheduledSubject.set(context, boundSubject());
      },
    }),
    defaultOptions: {
      queries: {
        retry: shouldRetryQuery,
        retryDelay: (attempt) => Math.min(250 * 2 ** attempt, 2_000),
        refetchOnWindowFocus: false,
        gcTime: 5 * 60 * 1_000,
      },
      mutations: {
        retry: false,
      },
    },
  });

export const createAppRouterContext = (): AppRouterContext => ({
  queryClient: createAppQueryClient(),
});
